import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Package, TrendingUp, CheckCircle,
  RefreshCw, Search,
  BarChart3, Calculator, ShoppingBag, XCircle, AlertCircle
} from 'lucide-react'
import { googleAdsApi } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'

// Formatting helpers
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

const formatPercent = (value: number) => {
  return `${value.toFixed(1)}%`
}

// Recommendation styles
const recommendationStyles: Record<string, { bg: string; text: string; border: string; label: string }> = {
  HIGHLY_RECOMMENDED: {
    bg: 'bg-success-100 dark:bg-green-900/30',
    text: 'text-green-800 dark:text-green-300',
    border: 'border-green-300 dark:border-green-700',
    label: 'Muy recomendado'
  },
  RECOMMENDED: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-800 dark:text-blue-300',
    border: 'border-blue-300 dark:border-blue-700',
    label: 'Recomendado'
  },
  NEUTRAL: {
    bg: 'bg-slate-100 dark:bg-slate-800/30',
    text: 'text-gray-700 dark:text-slate-400',
    border: 'border-slate-300 dark:border-gray-700',
    label: 'Neutral'
  },
  NOT_RECOMMENDED: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-800 dark:text-orange-300',
    border: 'border-orange-300 dark:border-orange-700',
    label: 'No recomendado'
  },
  EXCLUDE: {
    bg: 'bg-danger-100 dark:bg-danger-900/30',
    text: 'text-red-800 dark:text-red-300',
    border: 'border-red-300 dark:border-red-700',
    label: 'Excluir'
  }
}

// GMC status styles
const gmcStatusStyles: Record<string, { bg: string; text: string; label: string; iconType: 'check' | 'x' | 'alert' }> = {
  AVAILABLE: {
    iconType: 'check',
    bg: 'bg-success-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    label: 'En GMC'
  },
  INACTIVE: {
    iconType: 'x',
    bg: 'bg-slate-100 dark:bg-slate-800/30',
    text: 'text-slate-600 dark:text-slate-400',
    label: 'Inactivo'
  },
  NO_STOCK: {
    iconType: 'alert',
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-400',
    label: 'Sin stock'
  },
  LOW_PRICE: {
    iconType: 'alert',
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-400',
    label: 'Precio bajo'
  },
  NO_CATEGORY: {
    iconType: 'x',
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-400',
    label: 'Sin cat.'
  },
  EXCLUDED: {
    iconType: 'x',
    bg: 'bg-danger-100 dark:bg-danger-900/30',
    text: 'text-danger-700 dark:text-danger-400',
    label: 'Excluido'
  }
}

// Helper to get GMC icon
function GmcIcon({ type }: { type: 'check' | 'x' | 'alert' }) {
  if (type === 'check') return <CheckCircle className="w-4 h-4" />
  if (type === 'alert') return <AlertCircle className="w-4 h-4" />
  return <XCircle className="w-4 h-4" />
}

// Score indicator component
function ScoreIndicator({ score, label }: { score: number; label: string }) {
  const getColor = () => {
    if (score >= 80) return 'bg-success-500'
    if (score >= 60) return 'bg-blue-500'
    if (score >= 40) return 'bg-yellow-500'
    if (score >= 20) return 'bg-orange-500'
    return 'bg-danger-500'
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="flex items-center gap-2">
        <div className="w-16 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all", getColor())}
            style={{ width: `${score}%` }}
          />
        </div>
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300 w-8">
          {Math.round(score)}
        </span>
      </div>
    </div>
  )
}

// CPC Calculator Modal Component
function CpcCalculatorModal({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const [salePrice, setSalePrice] = useState<number>(50)
  const [costPrice, setCostPrice] = useState<number>(35)
  const [conversionRate, setConversionRate] = useState<number>(2)
  const [targetRoas, setTargetRoas] = useState<number>(400)

  const cpcQuery = useQuery({
    queryKey: ['cpc-calculation', salePrice, costPrice, conversionRate, targetRoas],
    queryFn: () => googleAdsApi.calculateMaxCpc(
      salePrice,
      costPrice,
      conversionRate / 100,
      targetRoas / 100
    ),
    enabled: isOpen && salePrice > 0 && costPrice > 0 && costPrice < salePrice
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg p-6">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Calculadora CPC Maximo
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Precio de venta (EUR)
              </label>
              <input
                type="number"
                value={salePrice}
                onChange={(e) => setSalePrice(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                step="0.01"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Precio de coste (EUR)
              </label>
              <input
                type="number"
                value={costPrice}
                onChange={(e) => setCostPrice(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                step="0.01"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Tasa de conversion (%)
              </label>
              <input
                type="number"
                value={conversionRate}
                onChange={(e) => setConversionRate(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                step="0.1"
                min="0"
                max="100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                ROAS objetivo (%)
              </label>
              <input
                type="number"
                value={targetRoas}
                onChange={(e) => setTargetRoas(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                step="10"
                min="100"
              />
            </div>

            {cpcQuery.data && (
              <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3">Resultado:</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-slate-600 dark:text-slate-400">Beneficio</div>
                    <div className="font-bold text-slate-900 dark:text-white">{formatCurrency(cpcQuery.data.profit)}</div>
                  </div>
                  <div>
                    <div className="text-slate-600 dark:text-slate-400">Margen</div>
                    <div className="font-bold text-slate-900 dark:text-white">{formatPercent(cpcQuery.data.margin_percent)}</div>
                  </div>
                  <div>
                    <div className="text-slate-600 dark:text-slate-400">CPC Maximo</div>
                    <div className="font-bold text-success-600 dark:text-success-400">{formatCurrency(cpcQuery.data.max_cpc)}</div>
                  </div>
                  <div>
                    <div className="text-slate-600 dark:text-slate-400">CPC Sugerido</div>
                    <div className="font-bold text-primary-600 dark:text-primary-400">{formatCurrency(cpcQuery.data.suggested_cpc)}</div>
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-600 dark:text-slate-400">
                  Formula: CPC_max = (beneficio * tasa_conversion) / (1 + ROAS_objetivo)
                </p>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function GoogleAdsProductsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRecommendation, setSelectedRecommendation] = useState<string | undefined>(undefined)
  const [selectedGmcStatus, setSelectedGmcStatus] = useState<string | undefined>(undefined)
  const [minScore, setMinScore] = useState<number | undefined>(undefined)
  const [showCpcCalculator, setShowCpcCalculator] = useState(false)

  // Queries
  const scoresQuery = useQuery({
    queryKey: ['google-ads-scores', selectedRecommendation, selectedGmcStatus, minScore],
    queryFn: () => googleAdsApi.getProductScores({
      recommendation: selectedRecommendation,
      gmc_status: selectedGmcStatus,
      min_score: minScore,
      limit: 100
    })
    // AdScores are calculated locally from Factusol data, no need for GA connection
  })

  const thresholdsQuery = useQuery({
    queryKey: ['google-ads-thresholds'],
    queryFn: googleAdsApi.getAdscoreThresholds
  })

  // Mutations
  const recalculateMutation = useMutation({
    mutationFn: googleAdsApi.recalculateScores,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-scores'] })
      toast.success('Recalculo de scores iniciado')
    },
    onError: (error: any) => {
      toast.error('Error iniciando recalculo', error.response?.data?.detail || error.message)
    }
  })

  const gmcStatusMutation = useMutation({
    mutationFn: googleAdsApi.updateGmcStatus,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-scores'] })
      toast.success(`Estado GMC actualizado: ${data.available} disponibles, ${data.unavailable} no disponibles`)
    },
    onError: (error: any) => {
      toast.error('Error verificando estado GMC', error.response?.data?.detail || error.message)
    }
  })

  // Filter products by search
  const filteredProducts = scoresQuery.data?.products?.filter(product => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      product.product_name?.toLowerCase().includes(search) ||
      product.reference?.toLowerCase().includes(search)
    )
  }) || []

  // Calculate summary stats
  const summaryStats = {
    total: scoresQuery.data?.total || 0,
    highlyRecommended: filteredProducts.filter(p => p.recommendation === 'HIGHLY_RECOMMENDED').length,
    recommended: filteredProducts.filter(p => p.recommendation === 'RECOMMENDED').length,
    avgScore: filteredProducts.length > 0
      ? filteredProducts.reduce((sum, p) => sum + p.ad_score, 0) / filteredProducts.length
      : 0
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Scores de Productos
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Analiza que productos son ideales para publicitar basandote en margenes, stock y ventas
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCpcCalculator(true)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            <Calculator className="w-4 h-4" />
            Calculadora CPC
          </button>
          <button
            onClick={() => recalculateMutation.mutate()}
            disabled={recalculateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={cn("w-4 h-4", recalculateMutation.isPending && "animate-spin")} />
            Recalcular Scores
          </button>
          <button
            onClick={() => gmcStatusMutation.mutate()}
            disabled={gmcStatusMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            title="Verificar disponibilidad en Google Merchant Center"
          >
            <ShoppingBag className={cn("w-4 h-4", gmcStatusMutation.isPending && "animate-spin")} />
            Verificar GMC
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Package className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">
                {summaryStats.total}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Total productos</div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">
                {summaryStats.highlyRecommended}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Muy recomendados</div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <TrendingUp className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">
                {summaryStats.recommended}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Recomendados</div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">
                {summaryStats.avgScore.toFixed(0)}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Score promedio</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre o referencia..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
            </div>
          </div>

          {/* Recommendation Filter */}
          <select
            value={selectedRecommendation || ''}
            onChange={(e) => setSelectedRecommendation(e.target.value || undefined)}
            className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          >
            <option value="">Todas las recomendaciones</option>
            <option value="HIGHLY_RECOMMENDED">Muy recomendado</option>
            <option value="RECOMMENDED">Recomendado</option>
            <option value="NEUTRAL">Neutral</option>
            <option value="NOT_RECOMMENDED">No recomendado</option>
            <option value="EXCLUDE">Excluir</option>
          </select>

          {/* GMC Status Filter */}
          <select
            value={selectedGmcStatus || ''}
            onChange={(e) => setSelectedGmcStatus(e.target.value || undefined)}
            className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          >
            <option value="">Todos los estados GMC</option>
            <option value="AVAILABLE">En GMC</option>
            <option value="NO_STOCK">Sin stock</option>
            <option value="LOW_PRICE">Precio bajo</option>
            <option value="NO_CATEGORY">Sin categoría</option>
            <option value="INACTIVE">Inactivo</option>
            <option value="EXCLUDED">Excluido</option>
          </select>

          {/* Min Score Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600 dark:text-slate-400">Score min:</label>
            <input
              type="number"
              value={minScore || ''}
              onChange={(e) => setMinScore(e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="0"
              className="w-20 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              min="0"
              max="100"
            />
          </div>

          <button
            onClick={() => {
              setSearchTerm('')
              setSelectedRecommendation(undefined)
              setSelectedGmcStatus(undefined)
              setMinScore(undefined)
            }}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
          >
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {scoresQuery.isLoading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-400">Cargando scores de productos...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              No hay productos
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {(scoresQuery.data as { message?: string })?.message || 'Ejecuta el recalculo de scores para analizar los productos.'}
            </p>
            <button
              onClick={() => recalculateMutation.mutate()}
              disabled={recalculateMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Recalcular ahora
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                    AdScore
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                    Componentes Score
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                    Margen
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                    Ventas 30d
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                    Stock
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                    CPC Sugerido
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                    Recomendacion
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                    GMC
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredProducts.map((product) => {
                  const style = recommendationStyles[product.recommendation] || recommendationStyles.NEUTRAL

                  return (
                    <tr key={product.id_product} className="hover:bg-slate-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-slate-900 dark:text-white">
                            {product.product_name || `Producto #${product.id_product}`}
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            Ref: {product.reference}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center">
                          <div className={cn(
                            "w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold",
                            product.ad_score >= 80 ? "bg-success-100 text-green-700 dark:bg-green-900/50 dark:text-green-300" :
                            product.ad_score >= 60 ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" :
                            product.ad_score >= 40 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300" :
                            product.ad_score >= 20 ? "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300" :
                            "bg-danger-100 text-danger-700 dark:bg-danger-900/50 dark:text-red-300"
                          )}>
                            {Math.round(product.ad_score)}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-4 justify-center">
                          <ScoreIndicator score={product.margin_score || 0} label="Marg." />
                          <ScoreIndicator score={product.volume_score || 0} label="Vol." />
                          <ScoreIndicator score={product.stock_score || 0} label="Stk." />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className={cn(
                          "font-medium",
                          (product.margin_percent || 0) >= 20 ? "text-success-600 dark:text-success-400" :
                          (product.margin_percent || 0) >= 10 ? "text-yellow-600 dark:text-yellow-400" :
                          "text-danger-600 dark:text-danger-400"
                        )}>
                          {formatPercent(product.margin_percent || 0)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className={cn(
                          "font-medium",
                          (product.sales_30d || 0) >= 10 ? "text-success-600 dark:text-success-400" :
                          (product.sales_30d || 0) >= 3 ? "text-yellow-600 dark:text-yellow-400" :
                          "text-slate-500 dark:text-slate-400"
                        )}>
                          {product.sales_30d || 0} uds
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className={cn(
                          "font-medium",
                          (product.stock_shared || 0) > 10 ? "text-success-600 dark:text-success-400" :
                          (product.stock_shared || 0) > 3 ? "text-yellow-600 dark:text-yellow-400" :
                          "text-danger-600 dark:text-danger-400"
                        )}>
                          {product.stock_shared || 0} uds
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {product.suggested_cpc ? (
                          <span className="font-medium text-primary-600 dark:text-primary-400">
                            {formatCurrency(product.suggested_cpc)}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={cn(
                          "inline-flex px-3 py-1 rounded-full text-xs font-medium",
                          style.bg,
                          style.text
                        )}>
                          {style.label}
                        </span>
                        {product.block_reason && (
                          <div className="mt-1 text-xs text-danger-600 dark:text-danger-400">
                            {product.block_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {product.gmc_status ? (
                          <div
                            className={cn(
                              "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                              gmcStatusStyles[product.gmc_status]?.bg || 'bg-slate-100',
                              gmcStatusStyles[product.gmc_status]?.text || 'text-slate-600'
                            )}
                            title={product.gmc_reason || undefined}
                          >
                            <GmcIcon type={gmcStatusStyles[product.gmc_status]?.iconType || 'x'} />
                            {gmcStatusStyles[product.gmc_status]?.label || product.gmc_status}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* AdScore Legend */}
      {thresholdsQuery.data && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Leyenda de AdScore</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {Object.entries(thresholdsQuery.data.thresholds).map(([key, value]) => {
              const style = recommendationStyles[key] || recommendationStyles.NEUTRAL
              return (
                <div key={key} className={cn("p-3 rounded-lg border", style.bg, style.border)}>
                  <div className={cn("font-medium", style.text)}>{style.label}</div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    Score {value.min}-{value.max}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                    {value.description}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* CPC Calculator Modal */}
      <CpcCalculatorModal
        isOpen={showCpcCalculator}
        onClose={() => setShowCpcCalculator(false)}
      />
    </div>
  )
}
